import type { LlmModel } from '@saaga/shared/interfaces/llm-model.interface'
import { type ReactNode, useMemo, useState } from 'react'

import { Command, CommandInput } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { createModelFilter } from './model-selector.helper'
import { ModelSelectorCommandContent } from './model-selector-command-content.atom'
import { ModelSelectorLoading } from './model-selector-loading.atom'
import { ModelSelectorTrigger } from './model-selector-trigger.atom'

export interface ModelSelectorSearchableProps {
  value: string
  onChange: (value: string) => void
  modelList: LlmModel[]
  isLoading?: boolean
  placeholder?: string
  isDisabled?: boolean
  className?: string
}

export const ModelSelectorSearchable = ({
  value,
  onChange,
  modelList,
  isLoading = false,
  placeholder = 'Select a model...',
  isDisabled = false,
  className,
}: ModelSelectorSearchableProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false)
  const selectedModel = modelList.find((model) => model.id === value)
  const filterFn = useMemo(() => createModelFilter(modelList), [modelList])

  if (isLoading) return <ModelSelectorLoading className={className} />

  const handleSelect = (currentValue: string): void => {
    onChange(currentValue === value ? '' : currentValue)
    setIsOpen(false)
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <ModelSelectorTrigger
          selectedModel={selectedModel}
          placeholder={placeholder}
          isOpen={isOpen}
          isDisabled={isDisabled}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="model-selector-searchable-content w-[var(--radix-popover-trigger-width)] p-0">
        <Command filter={filterFn}>
          <CommandInput
            placeholder="Search by name or ID..."
            className="model-selector-searchable-input h-9"
          />
          <ModelSelectorCommandContent
            modelList={modelList}
            selectedValue={value}
            onSelect={handleSelect}
          />
        </Command>
      </PopoverContent>
    </Popover>
  )
}

ModelSelectorSearchable.displayName = 'ModelSelectorSearchable'
